import { FunctionComponent, ReactNode, SVGProps, useCallback, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Card from '../../Card';
import { Cost } from '../../Cost';
import { LinkButton } from '../../LinkButton';
import classNames from 'classnames';
import Link from 'next/link';
import { useCheckoutEducatorLink } from '../useCheckoutEducatorLink';
import { useCheckoutCourseLink } from '../useCheckoutCourseLink';
import { PublicCourse } from '@cademy-package/sdk-component-types';

export type SummaryDetail = {
    icon: FunctionComponent<SVGProps<SVGSVGElement>>;
    detail: ReactNode;
};

export type CheckoutSummaryProps = {
    course: PublicCourse;
    taglineDetail: ReactNode;
    summarySections: Array<ReactNode>;
    totalCost?: number | string;
};

export const CheckoutSummary: FunctionComponent<CheckoutSummaryProps> = ({
    course,
    taglineDetail,
    totalCost,
    summarySections,
}) => {
    const educatorLink = useCheckoutEducatorLink();
    const courseLink = useCheckoutCourseLink();

    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded]);

    const totalCostNode: ReactNode = useMemo(() => {
        if (totalCost === undefined) {
            return null;
        }
        return <Cost value={Number(totalCost)} currency={course.currency} />;
    }, [totalCost, course.currency]);

    return (
        <Card
            id="checkoutSummary"
            className={classNames(styles.container, { [styles.expanded]: expanded })}
        >
            <div className={styles.heading}>
                <h2>
                    <Link href={courseLink}>{course.name}</Link>
                </h2>
                <p className={styles.subheading}>
                    By <Link href={educatorLink}>{course.educator.name}</Link>
                </p>
            </div>
            <div className={styles.taglineDetail}>{taglineDetail}</div>
            <div className={styles.sections}>
                {summarySections
                    .filter((section) => section !== null)
                    .map((section, index) => {
                        return <div key={index}>{section}</div>;
                    })}
            </div>
            <div className={styles.mobileFooter}>
                <span className={styles.mobileTotalCost}>Total: {totalCostNode}</span>
                <LinkButton onClick={toggleExpanded}>
                    {expanded ? 'Hide Summary' : 'View Full Summary'}
                </LinkButton>
            </div>
        </Card>
    );
};
